import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { useConfirm } from 'hooks/useConfirm';
import { useSelector } from 'hooks/useSelector';
import { doAddEvent, doRemoveEvent } from 'lib/events';
import { pureComponent } from 'lib/pureComponent';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { useDispatch } from 'react-redux';
import { doAssetLoadPlans, eventPinSelected } from 'store/actions';
import { selectAssetPlans } from 'store/assets/selectors';
import { PinNew, PinNewWithName } from 'store/plan/types';

interface Props {
  assetId: string;
  pin: PinNew | '';
  pins: PinNewWithName[];
  onChange: (pin: PinNew | '' | null) => void;
  showIconOnLeft?: boolean;
  style?: StyleProp<ViewStyle>;
}

export const PinField: React.FC<Props> = pureComponent(
  ({ assetId, onChange, pin, pins, showIconOnLeft = false, style }) => {
    const confirm = useConfirm();
    const dispatch = useDispatch();

    const selectionKey = React.useRef(`selectionKeyPinField-${Math.random()}`);
    const assetPlans = useSelector((state) => selectAssetPlans(state, assetId));

    const handleOpenPlan = () =>
      dispatch(
        doAssetLoadPlans({
          assetId,
          pin: pin || undefined,
          pins,
          selectionKey: selectionKey.current,
        }),
      );

    const handleDeselect = () =>
      confirm({
        text: 'Do you want to delete this pin?',
        confirmText: 'Delete',
        destructive: true,
        onConfirm: () => onChange(''),
      });

    const plan = pin
      ? Object.values(assetPlans).find(
          (assetPlan) => assetPlan.id === pin.planId,
        )
      : null;

    React.useEffect(() => {
      const pinEvent = doAddEvent(eventPinSelected, (action) => {
        if (action.payload.key === selectionKey.current) {
          onChange(action.payload.data || '');
        }
      });
      return () => {
        doRemoveEvent(pinEvent);
      };
    }, [onChange]);

    return (
      <CardRow
        label={plan && pin ? plan.name : 'Tap to Set'}
        labelColor={plan && pin ? undefined : theme.colors.placeholder}
        icon={showIconOnLeft ? ICONS.LocationPinLight : undefined}
        navigationType={showIconOnLeft ? 'none' : 'pin'}
        actionIcon={ICONS.CircleXmarkLight}
        onAction={pin ? handleDeselect : undefined}
        onPress={handleOpenPlan}
        style={style}
      />
    );
  },
);
